
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Company, findCompanyBySlug, mockCompanies } from '@/data/mockCompanies';
import { LocalStorageManager } from '@/utils/LocalStorageManager';
import { supabase } from '@/integrations/supabase/client';
import { getCachedCompany, setCachedCompany } from '@/utils/companyCache';

interface TenantContextType {
  currentCompany: Company | null;
  isLoadingCompany: boolean;
  isValidCompany: boolean;
  refreshCompany: () => void;
  updateCompanyData: (data: Partial<Company>) => Promise<void>;
  temporaryLayoutType: string | null;
  setTemporaryLayout: (layout: string) => void;
  resetLayout: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    // Safe fallback when used outside TenantProvider (e.g., /super-admin routes)
    return {
      currentCompany: null,
      isLoadingCompany: false,
      isValidCompany: false,
      refreshCompany: () => {},
      updateCompanyData: async () => {},
      temporaryLayoutType: null,
      setTemporaryLayout: () => {},
      resetLayout: () => {},
    } as TenantContextType;
  }
  return context;
};

// Export theme application function for direct use
export const applyCompanyTheme = (theme: { primaryColor: string; accentColor: string; mode?: 'light' | 'dark' }) => {
  const root = document.documentElement;
  const themeMode = theme.mode || 'dark';
  
  // Converter hex para HSL
  const hexToHsl = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '0 0% 50%';
    
    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };
  
  console.log('🎨 Applying client theme:', { 
    primary: hexToHsl(theme.primaryColor), 
    accent: hexToHsl(theme.accentColor),
    mode: themeMode 
  });
  
  // Apply to client-specific variables only
  root.style.setProperty('--client-primary', hexToHsl(theme.primaryColor));
  root.style.setProperty('--client-accent', hexToHsl(theme.accentColor));
  
  // Apply theme mode specific colors
  if (themeMode === 'light') {
    root.style.setProperty('--client-background', '0 0% 98%');
    root.style.setProperty('--client-foreground', '205 63% 13%');
    root.style.setProperty('--client-card', '0 0% 100%');
    root.style.setProperty('--client-card-foreground', '205 63% 13%');
    root.style.setProperty('--client-muted', '0 0% 96%');
    root.style.setProperty('--client-muted-foreground', '0 0% 45%');
    root.style.setProperty('--client-border', '0 0% 90%');
  } else {
    // Dark mode
    root.style.setProperty('--client-background', '205 63% 13%');
    root.style.setProperty('--client-foreground', '0 0% 100%');
    root.style.setProperty('--client-card', '205 63% 16%');
    root.style.setProperty('--client-card-foreground', '0 0% 100%');
    root.style.setProperty('--client-muted', '205 20% 25%');
    root.style.setProperty('--client-muted-foreground', '205 20% 80%');
    root.style.setProperty('--client-border', '205 30% 25%');
  }
  
  // Add client-theme class to body for scoped styling (only if not in admin context)
  const isAdminPath = window.location.pathname.startsWith('/admin') || 
                      window.location.pathname.startsWith('/super-admin');
  if (!isAdminPath) {
    document.body.classList.add('client-theme');
  }
};

export const useOptionalTenant = () => useContext(TenantContext);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { companySlug } = useParams<{ companySlug: string }>();
  const location = useLocation();
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [isValidCompany, setIsValidCompany] = useState(false);
  const [temporaryLayoutType, setTemporaryLayoutType] = useState<string | null>(null);

  const loadCompany = async (slug: string, retryCount = 0) => {
    // SEMPRE tentar usar cache primeiro (stale-while-revalidate)
    if (retryCount === 0) {
      const cachedCompany = getCachedCompany(slug, true);
      if (cachedCompany) {
        setCurrentCompany(cachedCompany);
        setIsValidCompany(true);
        setIsLoadingCompany(false);
        
        // Revalidar em segundo plano
        setTimeout(() => loadCompany(slug, 1), 100);
        return;
      }
    }
    
    setIsLoadingCompany(true);
    
    // Check for temporary company data (new companies)
    const tempCompanyKey = `digimenu_new_company_${slug}`;
    const tempCompanyData = localStorage.getItem(tempCompanyKey);
    
    if (tempCompanyData && retryCount === 0) {
      try {
        const tempCompany = JSON.parse(tempCompanyData);
        console.log('🆕 Found temporary company data, using as fallback');
        setCurrentCompany(tempCompany);
        setIsValidCompany(true);
        setIsLoadingCompany(false);
        
        // Continue loading from Supabase in background
        setTimeout(() => loadCompany(slug, 1), 1000);
        return;
      } catch (error) {
        console.error('Error parsing temporary company data:', error);
        localStorage.removeItem(tempCompanyKey);
      }
    }
    
    try {
      // Timeout otimizado para melhor performance
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao buscar empresa')), 5000)
      });
      
      // Primeiro tentar buscar no Supabase usando função segura
      console.log('🔍 Buscando empresa no Supabase...', slug);
      const queryPromise = supabase
        .rpc('get_company_by_slug', { company_slug: slug });
      
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]) as any;

      console.log('📊 Tipo de dados retornados:', typeof data);
      console.log('📊 Dados são array?:', Array.isArray(data));
      console.log('📊 Conteúdo completo dos dados:', JSON.stringify(data, null, 2));

      if (error) {
        console.error('❌ Erro ao buscar empresa no Supabase:', error);
        throw error;
      }

      let company: Company | null = null;

      // A função RPC pode retornar um array ou um objeto único
      if (data) {
        let supabaseCompany = null;
        
        if (Array.isArray(data) && data.length > 0) {
          console.log('🎯 Empresa encontrada no Supabase (formato array)');
          supabaseCompany = data[0];
        } else if (!Array.isArray(data) && typeof data === 'object') {
          console.log('🎯 Empresa encontrada no Supabase (formato objeto)');
          supabaseCompany = data;
        }
        
        if (supabaseCompany) {
          console.log('🏢 Dados da empresa encontrados:', supabaseCompany);

          // Criar ID sintético estável quando o Supabase não expõe o ID para usuários anônimos
          const isPublicViewer = !supabaseCompany.id;
          const syntheticId = `public-${slug}`;

          // Transformar dados do Supabase para o formato da aplicação
            const mappedCompany: Company = {
              id: supabaseCompany.id || syntheticId,
              name: supabaseCompany.name,
              slug: supabaseCompany.slug,
              email: supabaseCompany.email || '',
              phone: supabaseCompany.phone || '',
              address: supabaseCompany.address || '',
              workingHours: supabaseCompany.working_hours || 'Segunda a Domingo das 11h às 23h',
              logo: supabaseCompany.logo_url || '',
              logo_url: supabaseCompany.logo_url || '',
              banner_image_url: supabaseCompany.banner_image_url || '',
              theme: {
                primaryColor: supabaseCompany.theme_primary_color,
                accentColor: supabaseCompany.theme_accent_color,
                mode: supabaseCompany.theme_mode || 'dark'
              },
              theme_mode: supabaseCompany.theme_mode || 'dark',
              layout_type: supabaseCompany.layout_type || 'cards',
              enabled_layouts: supabaseCompany.enabled_layouts || [],
              // Mapear campos fragmentados de endereço (disponíveis para membros autenticados)
              street: supabaseCompany.street || '',
              number: supabaseCompany.number || '',
              complement: supabaseCompany.complement || '',
              neighborhood: supabaseCompany.neighborhood || '',
              city: supabaseCompany.city || '',
              state: supabaseCompany.state || '',
              zip_code: supabaseCompany.zip_code || '',
              // Mapear dados fiscais (disponíveis para membros autenticados)
              legal_name: supabaseCompany.legal_name || '',
              business_type: supabaseCompany.business_type || '',
              document_type: supabaseCompany.document_type || '',
              document_number: supabaseCompany.document_number || '',
              // Mapear links de redes sociais
              instagram_url: supabaseCompany.instagram_url || '',
              ifood_url: supabaseCompany.ifood_url || '',
              website_url: supabaseCompany.website_url || '',
              whatsapp_number: supabaseCompany.whatsapp_number || '',
              facebook_url: supabaseCompany.facebook_url || '',
              categories: [],
              menuItems: [],
              isActive: supabaseCompany.is_active,
              createdAt: new Date(supabaseCompany.created_at || new Date()),
              instagram_description: supabaseCompany.instagram_description || ''
            };
          
          company = mappedCompany;
          
          // Só buscar categorias e itens se a empresa estiver ativa usando função segura
          let menuData = { data: null as any };
          
          if (company.isActive) {
            // Use the secure function to get menu data without exposing company IDs
            const menuResp = await supabase
              .rpc('get_company_menu_data', { company_slug: slug });
            menuData = { data: menuResp.data };
          }

          // Extract data from secure function response - handle array response
          const menuRow = Array.isArray(menuData.data) ? menuData.data[0] : (menuData.data || {});
          const categories = Array.isArray(menuRow.categories_data) ? menuRow.categories_data : [];
          const menuItems = Array.isArray(menuRow.menu_items_data) ? menuRow.menu_items_data : [];
          const productTags = Array.isArray(menuRow.product_tags_data) ? menuRow.product_tags_data : [];

          console.log('📊 Menu data loaded:', {
            categories: categories.length,
            items: menuItems.length,
            tags: productTags.length
          });

          console.log('📋 Categorias encontradas:', categories.length);
          console.log('🍽️ Itens de menu encontrados:', menuItems.length);
          console.log('🏷️ Tags de produtos encontradas:', productTags.length);
          
          // Aceitar 0 items sem retry - não adicionar delay desnecessário
          
          // Transform categories - simplificado
          company.categories = categories.map((cat: any) => ({
            ...cat,
            order: cat.order_index,
          }));
          
          // Transform menu items - simplificado (banco já retorna formato correto)
          company.menuItems = menuItems.map((item: any) => ({
            ...item,
            category: item.category_id,
            image: item.image_url || '',
            isFeatured: item.is_featured,
            preparationTime: item.preparation_time,
          }));
        }
      } else {
        console.log('⚠️ Empresa não encontrada no Supabase, buscando nos dados mock');
        // Fallback para dados mock se não encontrou no Supabase
        let company = findCompanyBySlug(slug);
        
        if (company) {
          // Verificar se há dados salvos no localStorage para esta empresa
          const savedData = LocalStorageManager.getCompanyData(company.id);
          
          // Mesclar dados salvos com dados base
          if (Object.keys(savedData).length > 0) {
            company = { ...company, ...savedData };
          }
          setCurrentCompany(company);
        }
      }
      
      if (company) {
        console.log('✅ Empresa carregada com sucesso:', company.name);
        setCurrentCompany(company);
        setIsValidCompany(true);
        
        // Clean up ALL temporary data when real company is loaded successfully
        const tempKeys = [
          `digimenu_new_company_${slug}`,
          'temp_company_data', 
          'temp_company_id', 
          'temp_company_slug'
        ];
        
        tempKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            console.log('🧹 Cleaning up temporary data:', key);
            localStorage.removeItem(key);
          }
        });
        
        // Cache company data for better performance
        setCachedCompany(slug, company)
        
        // Se foi background refresh, forçar atualização da UI
        if (retryCount > 0) {
          console.log('🔄 Background refresh completado, atualizando UI');
        }
        
        // Aplicar tema da empresa no CSS (apenas se estiver no cardápio público)
        if (company.theme) {
          applyCompanyThemeInternal(company.theme);
        } else {
          // Se não houver tema, ainda assim verificar se devemos resetar
          const isPublicMenu = !window.location.pathname.includes('/admin') && 
                               !window.location.pathname.includes('/super-admin') &&
                               !window.location.pathname.includes('/auth') &&
                               !window.location.pathname.includes('/login') &&
                               window.location.pathname !== '/';
          
          if (!isPublicMenu) {
            resetClientTheme();
          }
        }
      } else {
        // Se empresa não encontrada e ainda não tentamos retry, tentar novamente (otimizado)
        if (retryCount < 1) {
          console.log('🔄 Empresa não encontrada, tentando novamente em 1 segundo...');
          setTimeout(() => {
            loadCompany(slug, retryCount + 1);
          }, 1000);
          return;
        }
        
        console.log('❌ Empresa não encontrada após tentativas');
        setCurrentCompany(null);
        setIsValidCompany(false);
      }
    } catch (error) {
      console.error('Erro ao carregar empresa:', error);
      
      // Fallback para dados mock em caso de erro
      const company = findCompanyBySlug(slug);
      if (company) {
        setCurrentCompany(company);
        setIsValidCompany(true);
        if (company.theme) {
          applyCompanyThemeInternal(company.theme);
        }
      } else {
        setCurrentCompany(null);
        setIsValidCompany(false);
      }
    } finally {
      setIsLoadingCompany(false);
    }
  };

  // Aplicar tema da empresa APENAS no contexto do cardápio público (internal wrapper)
  const applyCompanyThemeInternal = (theme: { primaryColor: string; accentColor: string; mode?: 'light' | 'dark' }) => {
    // Só aplicar tema se estiver visualizando o cardápio público
    const isPublicMenu = !window.location.pathname.includes('/admin') && 
                         !window.location.pathname.includes('/super-admin') &&
                         !window.location.pathname.includes('/auth') &&
                         !window.location.pathname.includes('/login') &&
                         window.location.pathname !== '/';
    
    if (!isPublicMenu) {
      console.log('🎨 Skipping theme application - not in public menu context');
      return;
    }
    
    // Call exported function
    applyCompanyTheme(theme);
  };
  
  // Reset client theme when leaving public menu
  const resetClientTheme = () => {
    const root = document.documentElement;
    
    // Reset to default red colors
    root.style.setProperty('--client-primary', '0 75% 56%');
    root.style.setProperty('--client-accent', '0 83% 49%');
    
    // Remove client-theme class
    document.body.classList.remove('client-theme');
    
    console.log('🎨 Client theme reset to default');
  };

  const refreshCompany = () => {
    if (companySlug) {
      loadCompany(companySlug);
    }
  };

  const setTemporaryLayout = (layout: string) => {
    setTemporaryLayoutType(layout);
  };

  const resetLayout = () => {
    setTemporaryLayoutType(null);
  };

  const updateCompanyData = async (data: Partial<Company>) => {
    if (currentCompany) {
      const updatedCompany = { ...currentCompany, ...data };
      setCurrentCompany(updatedCompany);
      
      // Salvar no localStorage
      LocalStorageManager.saveCompanyData(currentCompany.id, data);
      
      // Aplicar tema se foi atualizado
      if (data.theme) {
        console.log('🎨 Aplicando tema atualizado:', data.theme);
        applyCompanyThemeInternal(data.theme);
      }

      // Evitar persistir no Supabase se estivermos no menu público (id sintético)
      if (currentCompany.id.startsWith('public-')) {
        console.log('🛑 Ignorando persistência no Supabase no modo público (id sintético).');
        return;
      }
      
      // Persistir no Supabase
      try {
        console.log('💾 Persistindo dados no Supabase:', {
          company_id: currentCompany.id,
          theme_primary_color: data.theme?.primaryColor,
          theme_accent_color: data.theme?.accentColor,
          instagram_description: data.instagram_description
        });
        
        const { data: result, error } = await supabase.rpc(
          'update_company_info',
          {
            company_id_param: currentCompany.id,
            name_param: data.name || null,
            phone_param: data.phone || null,
            email_param: data.email || null,
            address_param: data.address || null,
            working_hours_param: data.workingHours || null,
            logo_url_param: (data as any).logoUrl || data.logo_url || null,
            theme_primary_color_param: data.theme?.primaryColor || null,
            theme_accent_color_param: data.theme?.accentColor || null,
            theme_mode_param: data.theme_mode || null,
            banner_image_url_param: (data as any).banner_image_url || null,
            // Individual address fields
            street_param: data.street || null,
            number_param: data.number || null,
            complement_param: data.complement || null,
            neighborhood_param: data.neighborhood || null,
            city_param: data.city || null,
            state_param: data.state || null,
            zip_code_param: data.zip_code || null,
            // Additional company fields
            legal_name_param: data.legal_name || null,
            business_type_param: data.business_type || null,
            document_type_param: data.document_type || null,
            document_number_param: data.document_number || null,
            instagram_description_param: data.instagram_description || null,
            enabled_layouts_param: data.enabled_layouts || null,
            // Social media links
            instagram_url_param: (data as any).instagram_url || null,
            ifood_url_param: (data as any).ifood_url || null,
            website_url_param: (data as any).website_url || null,
            whatsapp_number_param: (data as any).whatsapp_number || null,
            facebook_url_param: (data as any).facebook_url || null
          } as any
        );
        
        if (error) {
          console.error('❌ Erro na RPC function:', error);
          throw error;
        }
        
        console.log('✅ Dados da empresa atualizados no Supabase:', result);
        
      } catch (error) {
        console.error('❌ Erro ao atualizar empresa no Supabase:', error);
        throw error;
      }
    }
  };

  useEffect(() => {
    // Otimização: não recarregar empresa quando navegando dentro do admin
    const isAdminNavigation = location.pathname.includes('/admin') && currentCompany;
    if (isAdminNavigation) {
      console.log('⚡ Mantendo dados em cache - navegação no admin');
      return;
    }
    
    // Não executar se estivermos em rotas super-admin ou outras rotas especiais
    if (location.pathname.startsWith('/super-admin') || 
        location.pathname === '/' || 
        location.pathname === '/login' || 
        location.pathname === '/onboarding' ||
        location.pathname === '/unauthorized') {
      setCurrentCompany(null);
      setIsValidCompany(false);
      setIsLoadingCompany(false);
      return;
    }

    // Verificar se estamos em uma rota de empresa válida
    if (companySlug && 
        companySlug !== 'super-admin' && 
        companySlug !== 'login' && 
        companySlug !== 'onboarding' &&
        companySlug !== 'unauthorized') {
      console.log('🔄 TenantProvider: Carregando cardápio para:', companySlug);
      loadCompany(companySlug);
    } else {
      // Reset para rotas não relacionadas a empresas
      console.log('🔄 TenantProvider: Reset - não é rota de empresa');
      setCurrentCompany(null);
      setIsValidCompany(false);
      setIsLoadingCompany(false);
    }
  }, [companySlug, location.pathname]);

  // Cleanup theme when component unmounts
  useEffect(() => {
    return () => {
      resetClientTheme();
    };
  }, []);

  return (
    <TenantContext.Provider
      value={{
        currentCompany,
        isLoadingCompany,
        isValidCompany,
        refreshCompany,
        updateCompanyData,
        temporaryLayoutType,
        setTemporaryLayout,
        resetLayout,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
